import { range } from 'lodash-es';

import {
  isValid,
  isBefore,
  isSameDay,
  isAfter,
  startOfDay,
  endOfDay,
  startOfMonth,
  endOfMonth,
  getYear,
} from 'date-fns';

import {
  minYear,
  maxYear,
} from '@department-of-veterans-affairs/platform-forms-system/helpers';
import { dateFieldToDate } from '../../../../../utilities/date';

// Conditions for valid SSN from the original 1010ez pdf form:
// '123456789' is not a valid SSN
// A value where the first 3 digits are 0 is not a valid SSN
// A value where the 4th and 5th digits are 0 is not a valid SSN
// A value where the last 4 digits are 0 is not a valid SSN
// A value with 3 digits, an optional -, 2 digits, an optional -, and 4 digits is a valid SSN
// 9 of the same digits (e.g., '111111111') is not a valid SSN
export function isValidSSN(value) {
  if (
    value === '123456789' ||
    value === '123-45-6789' ||
    /^0{3}-?\d{2}-?\d{4}$/.test(value) ||
    /^\d{3}-?0{2}-?\d{4}$/.test(value) ||
    /^\d{3}-?\d{2}-?0{4}$/.test(value)
  ) {
    return false;
  }

  const noBadSameDigitNumber = range(0, 10).every(i => {
    const sameDigitRegex = new RegExp(`${i}{3}-?${i}{2}-?${i}{4}`);
    return !sameDigitRegex.test(value);
  });

  if (!noBadSameDigitNumber) {
    return false;
  }

  return /^\d{9}$/.test(value) || /^\d{3}-\d{2}-\d{4}$/.test(value);
}

// A 9 digit VA File Number must be an SSN
// The only other valid option is an 8-digit number
export function isValidVAFileNumber(value) {
  if (/^\d{9}$/.test(value) || /^\d{3}-\d{2}-\d{4}$/.test(value)) {
    return isValidSSN(value);
  }

  return /^\d{8}$/.test(value);
}

export function isValidYear(value) {
  return Number(value) >= minYear && Number(value) <= maxYear;
}

export function isValidPartialDate(day, month, year) {
  if (year && !isValidYear(year)) {
    return false;
  }

  return true;
}

export function isValidDate(day, month, year) {
  const date = new Date(year, parseInt(month, 10) - 1, day);
  return (
    isValid(date) &&
    date.getDate() === Number(day) &&
    date.getMonth() === parseInt(month, 10) - 1 &&
    date.getFullYear() === Number(year)
  );
}

export function isValidCurrentOrPastDate(day, month, year) {
  if (day && !isValidDate(day, month, year)) return false;
  // Partial/approximate dates omit the day (null); default it to the 1st of the
  // month so the constructed date represents the intended month rather than
  // wrapping to the last day of the prior month.
  const date = new Date(year, parseInt(month, 10) - 1, day || 1);
  const today = endOfDay(new Date());
  return isBefore(date, today) || isSameDay(date, today);
}

export function isValidCurrentOrFutureDate(day, month, year) {
  if (day && !isValidDate(day, month, year)) return false;
  // Partial/approximate dates omit the day (null); default it to the 1st of the
  // month (see isValidCurrentOrPastDate).
  const date = new Date(year, parseInt(month, 10) - 1, day || 1);
  const today = startOfDay(new Date());
  return isAfter(date, today) || isSameDay(date, today);
}

export function isValidCurrentOrPastYear(value) {
  return Number(value) >= minYear && Number(value) < getYear(new Date()) + 1;
}

export function isValidCurrentOrPastMonthYear(month, year) {
  const monthIndex = parseInt(month, 10) - 1;
  const date = new Date(year, monthIndex, 1);
  // Guard against month overflow: the Date constructor wraps out-of-range
  // months (e.g. month=0 => December of the prior year), which would otherwise
  // be reported as valid. Require the month to round-trip exactly.
  if (!isValid(date) || date.getMonth() !== monthIndex) return false;
  const currentMonthEnd = endOfMonth(new Date());
  return (
    isBefore(date, currentMonthEnd) || isSameDay(date, startOfMonth(new Date()))
  );
}

export function isValidCurrentOrFutureMonthYear(month, year) {
  const monthIndex = parseInt(month, 10) - 1;
  const date = new Date(year, monthIndex, 1);
  // Guard against month overflow (see isValidCurrentOrPastMonthYear).
  if (!isValid(date) || date.getMonth() !== monthIndex) return false;
  const currentMonthStart = startOfMonth(new Date());
  return isAfter(date, currentMonthStart) || isSameDay(date, currentMonthStart);
}

function isBlank(value) {
  return value === '';
}

function isBlankDateField(field) {
  return (
    isBlank(field.day.value) &&
    isBlank(field.month.value) &&
    isBlank(field.year.value)
  );
}

// To do: Fix
// allowSameMonth only checks whether the end/to date is the same as or after the
// start/from date, regardless of month, rename to allowSameDate or fix
export function isValidDateRange(fromDate, toDate, allowSameMonth = false) {
  if (isBlankDateField(toDate) || isBlankDateField(fromDate)) {
    return true;
  }

  const start = dateFieldToDate(fromDate);
  const end = dateFieldToDate(toDate);

  if (!isValid(start) || !isValid(end)) {
    return false;
  }

  return allowSameMonth
    ? isSameDay(end, start) || isBefore(start, end)
    : isBefore(start, end);
}

// Pulled from https://en.wikipedia.org/wiki/Routing_transit_number#Check_digit
export function isValidRoutingNumber(value) {
  if (/^\d{9}$/.test(value)) {
    const digits = value.split('').map(val => parseInt(val, 10));
    const weighted =
      3 * (digits[0] + digits[3] + digits[6]) +
      7 * (digits[1] + digits[4] + digits[7]) +
      (digits[2] + digits[5] + digits[8]);

    return weighted % 10 === 0;
  }
  return false;
}

export function isValidPartialMonthYear(month, year) {
  if (typeof month === 'object') {
    throw new Error('Pass a month and a year to function');
  }
  if (month && (Number(month) > 12 || Number(month) < 1)) {
    return false;
  }

  return isValidPartialDate(null, null, year);
}

export function isValidPartialMonthYearInPast(month, year) {
  if (typeof month === 'object') {
    throw new Error('Pass a month and a year to function');
  }
  if (!year) return true;

  const date = new Date(year, month ? parseInt(month, 10) - 1 : 0, 1);
  const currentMonthStart = startOfMonth(new Date());

  return (
    isValidPartialMonthYear(month, year) &&
    isValid(date) &&
    (isBefore(date, currentMonthStart) || isSameDay(date, currentMonthStart))
  );
}

export function isValidPartialMonthYearInFuture(month, year) {
  if (typeof month === 'object') {
    throw new Error(
      'isValidPartialMonthYearInFuture expects (month, year), got an object for month',
    );
  }
  if (!year) return true;

  // a year on its own counts while its December is still ahead
  return (
    isValidPartialMonthYear(month, year) &&
    isValidCurrentOrFutureMonthYear(month || 12, year)
  );
}

/**
 * Check validations for Custom pages
 * @param {Function[]} validations - array of validation functions
 * @param {*} data - field data passed to the validation function
 * @param {*} fullData - full and appStateData passed to validation function
 * @returns {String[]} - error messages
 */
export const getValidationErrors = (
  validations = [],
  data = {},
  fullData = {},
  index,
) => {
  const errors = { errorMessages: [] };
  errors.addError = message => errors.errorMessages.push(message);
  /* errors, fieldData, formData, schema, uiSchema, index, appStateData */
  validations.map(validation =>
    validation(errors, data, fullData, null, null, index, fullData),
  );
  return errors.errorMessages;
};
